import { track } from "@amplitude/analytics-browser";
import { Grid, Typography } from "@material-ui/core";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { MentorTip } from "../../../components";
import { FooterActions } from "../../../components/wizard/footer-actions.component";
import type { GamePlanPath } from "../../../components/wizard/use-game-plan";
import {
  AmplitudeEventType,
  type AmplitudeTrack,
} from "../../../models/amplitude";
import { Status } from "../../../models/gameplan";
import { getLearningPathProgress } from "../../../services";
import { usePreviousValue, useUpdateBffLearning } from "../../../shared/hooks";
import { useHistory } from "../../../shared/hooks/useHistory";
import { SuccessIcon } from "../../../shared/icons/gamePlan";
import { getActiveCourse } from "../../../shared/slices/activeCourse";
import {
  setCourseStatusById,
  setModuleStatusById,
} from "../../../shared/slices/data";
import type { RootState } from "../../../store";
import { useStyles } from "./completed-page.styles";

type Selector = {
  participantProfileId: RootState["profile"]["participantProfileId"];
};

interface CompletedPageProps {
  gamePlan: GamePlanPath;
  isGamePlanCompleted: boolean | undefined;
  isLearningPathFinished?: boolean | undefined;
}

export const CompletedPage = ({
  gamePlan,
  isLearningPathFinished,
  isGamePlanCompleted,
}: CompletedPageProps) => {
  const { module, label: type, parent } = gamePlan;
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { updateModuleStatus } = useUpdateBffLearning();
  const { courseId: urlCourseId } = useParams<{ courseId?: string }>();
  const defaultCourseId = useSelector(getActiveCourse);
  const courseId = (urlCourseId || defaultCourseId) as number;
  const prevGamePlanCompleted = usePreviousValue(isGamePlanCompleted);
  const trackEvent = !prevGamePlanCompleted && isGamePlanCompleted;

  const { participantProfileId } = useSelector<RootState, Selector>(
    (state) => ({
      participantProfileId: state.profile.participantProfileId,
    })
  );

  const onBackAction = () => {
    history.push("/participant/learning");
  };

  useEffect(() => {
    if (module.status === Status.COMPLETED) {
      const moduleId = module.item.id;
      dispatch(setModuleStatusById(moduleId, Status.COMPLETED));
      updateModuleStatus(moduleId, Status.COMPLETED);
    }

    if (type === "course") {
      dispatch(setCourseStatusById(courseId, Status.COMPLETED));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!trackEvent) return;

    getLearningPathProgress().then((data) => {
      const learningPath = data.learningPaths?.find(
        (learningPath) => learningPath.learningPathId === module.learningPath.id
      );

      if (learningPath?.completedPercentage >= 100) {
        const learningPathCompletedEvent: AmplitudeTrack<AmplitudeEventType.LEARNING_PATH_COMPLETED> =
          {
            event_type: AmplitudeEventType.LEARNING_PATH_COMPLETED,
            user_id: participantProfileId,
            event_properties: {
              page_url: window.location.href,
              learning_path_id: module.learningPath.id,
              learning_path_name: module.learningPath.name,
            },
          };
        track(learningPathCompletedEvent);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLearningPathFinished, trackEvent]);

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.title}>
            {type} Completed!
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <MentorTip
            variant="success"
            title={
              <Typography>
                <SuccessIcon />
                Congratulations, you've completed this {type}!
              </Typography>
            }
            details={
              <Typography className="details">
                You've acquired some useful knowledge about
                <b> {parent.title}</b>.
              </Typography>
            }
          />
        </Grid>
      </Grid>
      <FooterActions
        backText={type == "course" ? "Later" : undefined}
        backAction={type == "course" ? onBackAction : undefined}
        nextText={`Continue to next ${type}`}
      />
    </>
  );
};
